"use client";

import { useEffect, useRef, useState } from "react";
import cnBind from "classnames/bind";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { OverlayPanel } from "primereact/overlaypanel";

import {
    meNotificationsListApi,
    meNotificationsMarkReadApi,
    meNotificationsUnreadCountApi,
} from "@/api/meNotificationsApi/meNotificationsApi";
import { ADMIN_PATHS } from "@/shared/constants/adminPaths";
import { NOTIFICATION_CATEGORY_LABELS } from "@/shared/constants/notificationCategories";
import { useToast } from "@/shared/context";
import { Button } from "@/shared/ui/Button";

import styles from "./AdminNotificationsBell.module.scss";

const cx = cnBind.bind(styles);

type Props = {
    /** toolbar — светлая кнопка (шапка / кабинет); sidebar — блок в тёмном сайдбаре */
    variant?: "toolbar" | "sidebar";
    sidebarCollapsed?: boolean;
    /** Inbox page for «Мои уведомления». Cabinet must not use /admin (staff-only). */
    inboxHref?: string;
};

export function AdminNotificationsBell({
    variant = "toolbar",
    sidebarCollapsed = false,
    inboxHref = ADMIN_PATHS.notifications,
}: Props) {
    const op = useRef<OverlayPanel>(null);
    const queryClient = useQueryClient();
    const toast = useToast();
    const [panelOpen, setPanelOpen] = useState(false);

    const { data: countData } = useQuery({
        queryKey: ["me-notifications", "unread-count"],
        queryFn: meNotificationsUnreadCountApi,
        refetchInterval: panelOpen ? 15_000 : 60_000,
    });

    const { data: listData, isFetching } = useQuery({
        queryKey: ["me-notifications", "list", "popover"],
        queryFn: () => meNotificationsListApi({ take: 25 }),
        enabled: panelOpen,
    });

    const markReadMut = useMutation({
        mutationFn: (payload: { ids?: string[]; all?: boolean }) => meNotificationsMarkReadApi(payload),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ["me-notifications"] });
        },
        onError: (e: unknown) => {
            toast?.({
                severity: "error",
                summary: "Ошибка",
                detail: e instanceof Error ? e.message : String(e),
            });
        },
    });

    const unread = countData?.count ?? 0;
    const items = listData?.items ?? [];
    const isSidebar = variant === "sidebar";

    useEffect(() => {
        if (!panelOpen) return;
        const align = () => op.current?.align();
        const t0 = requestAnimationFrame(align);
        const t1 = window.setTimeout(align, 30);
        const t2 = window.setTimeout(align, 120);
        const t3 = window.setTimeout(align, 320);
        return () => {
            cancelAnimationFrame(t0);
            window.clearTimeout(t1);
            window.clearTimeout(t2);
            window.clearTimeout(t3);
        };
    }, [panelOpen, isFetching, items.length]);

    return (
        <div className={cx("bellWrap", { bellWrapSidebar: isSidebar })}>
            <button
                type="button"
                className={cx("bellBtn", { bellBtnSidebar: isSidebar })}
                aria-label="Уведомления"
                aria-expanded={panelOpen}
                onClick={(e) => op.current?.toggle(e)}
            >
                <span aria-hidden className={cx("bellEmoji")}>
                    🔔
                </span>
                {!sidebarCollapsed ? (
                    <span className={cx("bellLabel")}>Уведомления</span>
                ) : null}
                {unread > 0 ? (
                    <span className={cx("badge", { badgeSidebar: isSidebar })}>{unread > 99 ? "99+" : unread}</span>
                ) : null}
            </button>
            <OverlayPanel
                ref={op}
                className={cx("overlayRoot")}
                appendTo={typeof document !== "undefined" ? document.body : undefined}
                onShow={() => setPanelOpen(true)}
                onHide={() => setPanelOpen(false)}
            >
                <div className={cx("panel")}>
                    <div className={cx("panelActions")}>
                        <Button
                            label="Прочитать все"
                            variant="outlined"
                            iconTrue={false}
                            disabled={markReadMut.isPending || unread === 0}
                            onClick={() => markReadMut.mutate({ all: true })}
                        />
                        <Link
                            href={inboxHref}
                            className={cx("centerLink")}
                            onClick={(e) => {
                                // Hard nav: Soft App Router inside cabinet often remounts the wrong section.
                                if (inboxHref.startsWith("/cabinet")) {
                                    e.preventDefault();
                                    window.location.assign(inboxHref);
                                }
                            }}
                        >
                            Мои уведомления
                        </Link>
                    </div>
                    {isFetching ? <div className={cx("empty")}>Загрузка…</div> : null}
                    {!isFetching && items.length === 0 ? <div className={cx("empty")}>Пока нет уведомлений</div> : null}
                    {items.map((n) => (
                        <div key={n.id} className={cx("item", { itemUnread: !n.readAt })}>
                            <span className={cx("catPill")}>
                                {NOTIFICATION_CATEGORY_LABELS[n.category ?? "system"] ?? n.category ?? "system"}
                            </span>
                            <p className={cx("itemTitle")}>{n.title}</p>
                            <p className={cx("itemBody")}>{n.body}</p>
                            {(() => {
                                const p = n.payload as { sessionId?: string } | null | undefined;
                                const sid = typeof p?.sessionId === "string" ? p.sessionId.trim() : "";
                                if (!sid) return null;
                                return (
                                    <div className={cx("itemChatLink")}>
                                        <Link href={`${ADMIN_PATHS.chat}?session=${encodeURIComponent(sid)}`}>
                                            Открыть диалог в чатах
                                        </Link>
                                    </div>
                                );
                            })()}
                            <div className={cx("itemMeta")}>
                                {new Date(n.createdAt).toLocaleString("ru-RU")}
                                {!n.readAt ? (
                                    <>
                                        {" · "}
                                        <button
                                            type="button"
                                            className={cx("readLink")}
                                            onClick={() => markReadMut.mutate({ ids: [n.id] })}
                                        >
                                            Прочитано
                                        </button>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    ))}
                </div>
            </OverlayPanel>
        </div>
    );
}
